import { Overlay } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import * as React from 'react';
import { bareListDecorator } from 'webapp/decorators/list';
import { overflowYDecorator } from 'webapp/decorators/overflow';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { ZIndex } from 'webapp/z-index';
import { Theme } from 'webapp/theme';
import { currentUrl, translate } from '@avensia/excite';
import { SEARCH_PARAMS_SORT_BY } from '@avensia/excite-esales';
import { CloseIcon } from 'webapp/components/icons';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import { white } from 'webapp/shared/sharedTheme';
import { Button } from 'webapp/components/button';
import { FieldSize, createFieldSizeDecorator } from 'webapp/components/field-size';
import { getSortOptionTestIdProps } from 'webapp/shared/test-ids';

type PropType = {
  close: () => void;
  onChangeSort: (value: string, text: string) => void;
  options: NordicNest.Api.SelectOptionApiModel[];
  isOpen: boolean;
};

export const SortMobile = ({ close, onChangeSort, isOpen, options }: PropType) => {
  const defaultSortOptions = currentUrl().searchParams.get(SEARCH_PARAMS_SORT_BY) || 'relevance';
  const [value, setValue] = React.useState(defaultSortOptions);
  const modalRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (isOpen) {
      const modalElement = modalRef.current;
      const focusableElements = modalElement?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const firstElement = focusableElements?.[0];
      const lastElement = focusableElements?.[focusableElements.length - 1];

      const handleTabKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            (lastElement as HTMLElement).focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            (firstElement as HTMLElement).focus();
          }
        }
      };

      modalElement?.addEventListener('keydown', handleTabKeyPress);

      return () => {
        modalElement?.removeEventListener('keydown', handleTabKeyPress);
      };
    }
  }, [isOpen, close]);

  return (
    <Options disableFreezeScroll zIndex={ZIndex.Overlay} onClose={close} isEnabled={isOpen}>
      <Container ref={modalRef}>
        <Header>
          <Label>{translate(t => t.productListing.sortButtonText)}</Label>
          <CloseButton
            as={Theme.Transparent}
            onClick={close}
            square
            aria-label={translate(t => t.ariaLabelCloseButton)}
          >
            <CloseIcon />
          </CloseButton>
        </Header>
        <ScrollContainer>
          <Dropdown role="listbox">
            {options?.map((option, index) => (
              <Option
                tabIndex={0}
                role="option"
                key={option.value}
                aria-selected={value === option.value ? 'true' : 'false'}
                css={createSelectDecorator(option.value === value)}
                onKeyDown={e => {
                  if (e.code === 'Enter') {
                    setValue(option.value);
                    onChangeSort(option.value, option.text);
                    close();
                  }
                }}
                onClick={() => {
                  setValue(option.value);
                  onChangeSort(option.value, option.text);
                  close();
                }}
                {...getSortOptionTestIdProps(option)}
              >
                {option.text}
              </Option>
            ))}
          </Dropdown>
        </ScrollContainer>
      </Container>
    </Options>
  );
};

const Header = styled.div({
  backgroundColor: white,
  display: 'grid',
  gridTemplateAreas: '". label close"',
  gridTemplateColumns: '58px 2fr 58px',
  textAlign: 'center',
  border: { bottom: { width: 1, style: 'solid', color: t => t.facets?.borderColor } },
  alignItems: 'center',
});

const Label = styled.h1({
  padding: { y: t => t.sharedSpacing.m },
  fontSize: t => t.facets?.flyoutHeader?.fontSize,
  fontWeight: t => t.facets?.flyoutHeader?.fontWeight,
  gridArea: 'label',
  marginBottom: 0,
});

const CloseButton = styled(Button, {
  gridArea: 'close',
  padding: { x: t => t.spacing.xs },
});

function createSelectDecorator(selected: boolean) {
  return selected
    ? styled({
        backgroundColor: t => t.monochrome.color,
      })
    : styled();
}

const Container = styled.div({
  position: 'absolute',
  width: '100%',
  bottom: 0,
  maxHeight: '60vh',
  backgroundColor: 'white',
});

const ScrollContainer = styled.div(scrollbarDecorator, overflowYDecorator, {
  maxHeight: 'inherit',
});

const Dropdown = styled.ul(bareListDecorator, {
  backgroundColor: 'white',
  width: '100%',
  height: 'inherit',
});

const Option = styled.li(overflowEllipsisDecorator, createFieldSizeDecorator(FieldSize.Small), {
  cursor: 'pointer',
  borderBottom: { width: 'thin', style: 'solid', color: t => t.facets?.borderColor },
  padding: { y: t => t.sharedSpacing.m },
  ':last-child': {
    borderBottom: { width: 0 },
  },
});

const Options = styled(Overlay);
